import {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import {
  getEventCircleInvitation,
  respondToEventCircle,
} from "./eventCircle.api";

import type {
  EventCircle,
  EventCircleResponse,
} from "./eventCircle.types";

import {
  formatEventDateTime,
  responseLabel,
} from "./eventCircle.types";

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Could not open this Event Circle invitation.";
}

export function EventCircleInvitationPage() {
  const { token = "" } = useParams();

  const [circle, setCircle] =
    useState<EventCircle | null>(null);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [message, setMessage] = useState("");

  async function load() {
    if (!token) {
      setError("This invitation link is incomplete.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result =
        await getEventCircleInvitation(token);

      setCircle({
        ...result,
        token,
      });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [token]);

  async function respond(
    status: Exclude<EventCircleResponse, "AWAITING">,
  ) {
    if (!token) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const updated =
        await respondToEventCircle(token, status);

      setCircle({
        ...updated,
        token,
      });

      setMessage(
        `Your response is ${responseLabel(status)}.`,
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="event-circle-page">
      <header className="event-circle-page__header">
        <div>
          <span>EVENT CIRCLE INVITATION</span>

          <h1>Shared moment</h1>

          <p>
            Open the event details and choose your
            response.
          </p>
        </div>

        <div className="event-circle-page__actions">
          <Link to="/app/event-circle">
            My Event Circles
          </Link>
        </div>
      </header>

      {loading ? (
        <section className="event-circle-empty">
          <h2>Opening invitation…</h2>
        </section>
      ) : error ? (
        <section className="event-circle-empty">
          <h2>Invitation unavailable</h2>

          <p>{error}</p>

          <div className="event-circle-page__actions">
            <button
              type="button"
              onClick={() => void load()}
            >
              Try again
            </button>
          </div>
        </section>
      ) : circle ? (
        <section className="event-circle-card">
          <div className="event-circle-card__top">
            <div className="event-circle-card__title">
              <span className="event-circle-card__eyebrow">
                {circle.visibility === "PUBLIC"
                  ? "PUBLIC EVENT"
                  : "PRIVATE INVITATION"}
              </span>

              <h2>{circle.title}</h2>
            </div>

            <span className="event-circle-card__status">
              {circle.status}
            </span>
          </div>

          <div className="event-circle-card__meta">
            <span>
              Starts{" "}
              {formatEventDateTime(circle.startsAt)}
            </span>

            <span>
              Ends{" "}
              {formatEventDateTime(circle.endsAt)}
            </span>

            {circle.location && (
              <span>{circle.location}</span>
            )}
          </div>

          {circle.description && (
            <p className="event-circle-card__description">
              {circle.description}
            </p>
          )}

          {circle.status === "CANCELLED" ? (
            <p className="event-circle-card__description">
              This Event Circle was cancelled by its
              host.
            </p>
          ) : (
            <>
              <p className="event-circle-card__description">
                Your current response:{" "}
                <strong>
                  {responseLabel(
                    circle.responseStatus,
                  )}
                </strong>
              </p>

              <div className="event-circle-response-actions">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void respond("GOING")}
                >
                  Going
                </button>

                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void respond("MAYBE")}
                >
                  Maybe
                </button>

                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void respond("BUSY")}
                >
                  Busy
                </button>

                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    void respond("NOT_GOING")
                  }
                >
                  Not going
                </button>
              </div>
            </>
          )}

          {message && (
            <p className="event-circle-card__description">
              {message}
            </p>
          )}
        </section>
      ) : null}
    </main>
  );
}